'use client';
import { Fragment, useEffect, useId, useRef, useState, type KeyboardEvent } from 'react';
import { Lock, Pencil, Star } from 'lucide-react';
import { answerReady } from '../../lib/askReadiness';
import type { AskAnswer, AskQuestion } from '../../lib/types';
import { Button } from '../../components/ui/Button';
import { StatusMark } from '../../components/ui/StatusMark';
import { HelpTip } from '../../components/ui/HelpTip';
import { Input } from '../../components/ui/Input';
import { Textarea } from '../../components/ui/shadcn/textarea';
import { RadioGroup, RadioGroupItem } from '../../components/ui/shadcn/radio-group';
import { Checkbox } from '../../components/ui/shadcn/checkbox';
import { Item } from '../../components/ui/shadcn/item';
import { useTranslation } from '../../lib/i18n';
import { interpolate } from '../../lib/i18n/interpolate';
import { useOperationFailure, useToast } from '../../components/ui/Toast';
import { holdsAnswer, releasing } from '../../lib/saveFeedback';

/** The record a plugin question asks the user to confirm (`AskQuestionDetail`, e.g. an outgoing email):
 *  labelled facts, then the complete body. Every value is a React text node with its whitespace kept,
 *  never markdown or HTML, because the reader confirms exactly what will be acted on. A long body
 *  scrolls inside its own keyboard-reachable region instead of being cut. */
function QuestionDetail({ detail, index }: { detail: NonNullable<AskQuestion['detail']>; index: number }) {
  const { t } = useTranslation();
  return <div data-testid={`ask-detail-${index}`} className="min-w-0 overflow-hidden rounded-lg border border-hairline bg-sunken">
    {detail.fields.length ? <dl className="grid grid-cols-[auto_minmax(0,1fr)] gap-x-3 gap-y-1 px-3 py-2.5 text-meta">
      {detail.fields.map((field, fieldIndex) => <Fragment key={fieldIndex}>
        <dt className="text-muted-foreground">{field.label}</dt>
        <dd className="min-w-0 whitespace-pre-wrap break-words text-foreground">{field.value}</dd>
      </Fragment>)}
    </dl> : null}
    {detail.body !== undefined ? <div data-testid={`ask-detail-body-${index}`} tabIndex={0} role="group" aria-label={t.brainChat.askDetailBody}
      className={`max-h-[40dvh] overflow-y-auto whitespace-pre-wrap break-words px-3 py-2.5 text-body text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-ring ${detail.fields.length ? 'border-t border-hairline' : ''}`}>
      {detail.body}
    </div> : null}
  </div>;
}

function QuestionSection({ question: q, index, total, selected, otherOpen, other, disabled, approval, onPick, onCustom, onOther }: {
  question: AskQuestion; index: number; total: number; selected: string[]; otherOpen: boolean; other: string; disabled: boolean; approval: boolean;
  onPick: (label: string) => void; onCustom: () => void; onOther: (text: string) => void;
}) {
  const { t } = useTranslation();
  const id = useId();
  const listRef = useRef<HTMLDivElement>(null);
  const [cursor, setCursor] = useState(0);
  const customValue = String(q.options.length);
  const count = q.options.length + (q.custom === false ? 0 : 1);
  const pick = (index: number) => {
    setCursor(index);
    if (index === q.options.length) onCustom();
    else onPick(q.options[index].label);
  };
  const keyboard = (event: KeyboardEvent<HTMLElement>) => {
    if (disabled || event.target instanceof HTMLInputElement || event.target instanceof HTMLTextAreaElement) return;
    const number = Number(event.key);
    if (!event.ctrlKey && !event.metaKey && !event.altKey && number >= 1 && number <= count) {
      event.preventDefault();
      const index = number - 1;
      listRef.current!.querySelectorAll<HTMLButtonElement>('[data-choice]')[index].focus();
      pick(index);
    } else if (q.multiSelect && !event.altKey && ['ArrowDown', 'ArrowRight', 'ArrowUp', 'ArrowLeft', 'Home', 'End'].includes(event.key)) {
      event.preventDefault();
      const next = event.key === 'Home' ? 0 : event.key === 'End' ? count - 1
        : (cursor + (event.key === 'ArrowUp' || event.key === 'ArrowLeft' ? -1 : 1) + count) % count;
      setCursor(next);
      listRef.current!.querySelectorAll<HTMLButtonElement>('[data-choice]')[next].focus();
    }
  };
  const selectedOption = !q.multiSelect && !otherOpen ? q.options.find(option => selected.includes(option.label)) : undefined;
  const preview = (mobile: boolean) => selectedOption?.preview ? (
    <div data-testid={mobile ? `ask-preview-mobile-${index}` : `ask-preview-${index}`}
      className={`min-w-0 rounded-lg border border-hairline bg-sunken p-3 ${mobile ? 'mt-2 sm:hidden' : 'hidden self-start sm:block'}`}>
      <div className="mb-2 text-small text-muted-foreground">{t.brainChat.askPreview} · {selectedOption.label}</div>
      {/* The event contract makes a preview monospace art (src/brain/events.ts), drawn verbatim as the CLI does:
          markdown would reflow the lines and spacing a mockup exists to show. */}
      <pre className="m-0 max-w-full overflow-x-auto whitespace-pre font-mono text-code text-foreground">{selectedOption.preview}</pre>
    </div>
  ) : null;
  const rows = <>
    {q.options.map((option, oi) => {
      const checked = selected.includes(option.label);
      const label = !approval ? option.label : option.id === 'once' ? t.brainChat.approvalOnce
        : option.id === 'conversation' ? t.brainChat.ux.approvalConversation
        : option.id === 'always' ? t.brainChat.approvalAlways : t.brainChat.approvalDeny;
      // What a remembered approval covers is read on demand from a HelpTip beside the scope, not printed
      // under it; a plugin's own option description is its content and stays visible.
      const scope = approval && option.id === 'conversation' && q.approval?.alwaysPattern
        ? interpolate(t.brainChat.ux.approvalConversationHint, { pattern: q.approval.alwaysPattern })
        : approval && option.id === 'always' && q.approval?.alwaysPattern
          ? interpolate(t.brainChat.approvalAlwaysHint, { pattern: q.approval.alwaysPattern }) : undefined;
      const description = approval ? undefined : option.description;
      return <div key={option.label} className={approval && option.id === 'deny' ? 'mt-2' : undefined}>
        <Item variant="selectable" size="sm" role="presentation" data-selected={checked} data-disabled={disabled}
          className="min-h-[var(--touch-target)] flex-nowrap items-start gap-2.5 px-2.5 py-2">
          <label htmlFor={`${id}-${oi}`} className="flex min-w-0 flex-1 items-start gap-2.5">
            {q.multiSelect
              ? <Checkbox id={`${id}-${oi}`} data-choice aria-label={label} checked={checked} disabled={disabled}
                  tabIndex={cursor === oi ? 0 : -1} onFocus={() => setCursor(oi)} onCheckedChange={() => pick(oi)} className="mt-0.5" />
              : <RadioGroupItem id={`${id}-${oi}`} data-choice aria-label={label} value={String(oi)} disabled={disabled} className="mt-0.5" />}
            <span className="flex min-w-0 flex-1 flex-col gap-0.5">
              <span className="flex flex-wrap items-center gap-x-2 gap-y-1 text-ui">
                <span className="break-words">{label}</span>
                {scope ? <HelpTip align="left" label={`${label}: ${t.common.help}`}>{scope}</HelpTip> : null}
              </span>
              {description ? <span className="text-meta text-muted-foreground">{description}</span> : null}
            </span>
            <kbd aria-hidden className="hidden text-small text-subtle-foreground sm:block">{oi + 1}</kbd>
          </label>
          {option.recommended ? <StatusMark icon={Star} tone="accent" label={t.brainChat.askRecommended} /> : null}
        </Item>
        {checked && !q.multiSelect ? preview(true) : null}
      </div>;
    })}
    {q.custom !== false ? <Item variant="selectable" size="sm" role="presentation" data-selected={otherOpen} data-disabled={disabled}
      className="min-h-[var(--touch-target)] flex-col items-stretch gap-2 px-2.5 py-2">
      <label htmlFor={`${id}-custom`} className="flex min-h-6 cursor-pointer items-center gap-2.5 text-ui">
        {q.multiSelect
          ? <Checkbox id={`${id}-custom`} data-choice aria-label={t.brainChat.askOther} checked={otherOpen} disabled={disabled}
              tabIndex={cursor === q.options.length ? 0 : -1} onFocus={() => setCursor(q.options.length)} onCheckedChange={onCustom} />
          : <RadioGroupItem id={`${id}-custom`} data-choice aria-label={approval ? t.brainChat.ux.approvalInstead : t.brainChat.askOther} value={customValue} disabled={disabled} />}
        <Pencil size={14} className="text-muted-foreground" aria-hidden />
        <span>{approval ? t.brainChat.ux.approvalInstead : t.brainChat.askOther}</span>
      </label>
      {otherOpen ? approval ? <>
        <Textarea autoFocus aria-label={t.brainChat.ux.approvalInstruction} placeholder={t.brainChat.ux.approvalInstruction}
          aria-invalid={other.trim().length > 2000} aria-describedby={other.trim().length > 2000 ? `${id}-feedback-hint` : undefined}
          value={other} disabled={disabled} onChange={event => onOther(event.target.value)} />
        {other.trim().length > 2000 ? <p id={`${id}-feedback-hint`} className="text-meta text-destructive">{t.brainChat.ux.approvalFeedbackRequired}</p> : null}
      </> : <Input autoFocus aria-label={t.brainChat.askOtherPlaceholder} placeholder={t.brainChat.askOtherPlaceholder}
        value={other} disabled={disabled} onChange={event => onOther(event.target.value)} /> : null}
    </Item> : null}
  </>;
  return <section tabIndex={-1} onKeyDown={keyboard} className="min-w-0 space-y-2 focus:outline-none" aria-labelledby={`${id}-question`}>
    <div className="space-y-0.5">
      <p className="flex items-baseline justify-between gap-3 text-meta text-muted-foreground">
        <span className="min-w-0 break-words">{approval ? t.brainChat.approvalWaiting : q.header}{q.multiSelect ? ` · ${t.brainChat.askMulti}` : ''}</span>
        {total > 1 ? <span aria-live="polite" className="shrink-0 tabular-nums">
          <span aria-hidden>{index + 1} / {total}</span>
          <span className="sr-only">{interpolate(t.brainChat.askQuestionOf, { current: index + 1, total })}</span>
        </span> : null}
      </p>
      <h3 id={`${id}-question`} className="text-section-title">{approval && q.approval
        ? q.approval.command ? t.brainChat.approvalRunCommand : interpolate(t.brainChat.approvalRunTool, { tool: q.approval.tool }) : q.question}</h3>
    </div>
    {approval && q.approval?.command ? <pre className="max-w-full overflow-x-auto rounded-md bg-sunken px-3 py-2 font-mono text-meta text-foreground"><code>$ {q.approval.command}</code></pre> : null}
    {approval && q.approval?.warning ? <p className="text-meta text-warning">{interpolate(t.brainChat.approvalWarningNote, { note: t.brainChat.approvalWarnings[q.approval.warning] })}</p> : null}
    {q.detail ? <QuestionDetail detail={q.detail} index={index} /> : null}
    <div className={selectedOption?.preview ? 'grid gap-3 sm:grid-cols-[minmax(0,1fr)_minmax(0,1.1fr)]' : ''}>
      <div ref={listRef} className="min-w-0">
        {q.multiSelect
          ? <div role="group" aria-labelledby={`${id}-question`} className="space-y-0.5">{rows}</div>
          : <RadioGroup aria-labelledby={`${id}-question`} value={otherOpen ? customValue : selected.length ? String(q.options.findIndex(option => option.label === selected[0])) : ''}
              onValueChange={value => pick(Number(value))} disabled={disabled} className="gap-0.5">{rows}</RadioGroup>}
      </div>
      {preview(false)}
    </div>
  </section>;
}

/** Inline question/approval surface, drawn as transcript content with no frame of its own. Several questions
 *  page one at a time; answers live here, so paging never loses one. Result details, not this ephemeral form,
 *  own answered records. */
export function AskQuestionCard({ questions, kind, readOnly = false, onSubmit, onReload }: {
  questions: AskQuestion[]; kind?: 'approval'; readOnly?: boolean; onSubmit: (answers: AskAnswer[]) => Promise<void>;
  /** The authoritative re-read of whether this question is still parked: the one recovery of an answer whose
   *  outcome is unknown. It must really re-read the daemon's pending question. */
  onReload: () => Promise<void>;
}) {
  const { t } = useTranslation();
  const { dismissKey } = useToast();
  const reportFailure = useOperationFailure();
  const failureKey = `ask-answer:${useId()}`;
  const [page, setPage] = useState(0);
  const [picked, setPicked] = useState<Record<number, string[]>>({});
  const [custom, setCustom] = useState<Record<number, boolean>>({});
  const [other, setOther] = useState<Record<number, string>>({});
  const [pending, setPending] = useState(false);
  // A failed answer is reported once, by the toast; `failed` only keeps the way to try again. `held`: the
  // outcome is unknown, so the same answer is not offered again until the daemon has been asked.
  const [failed, setFailed] = useState(false);
  const [held, setHeld] = useState(false);
  const [expired, setExpired] = useState(false);
  const sending = useRef(false);
  const body = useRef<HTMLDivElement>(null);
  const paged = useRef(false);
  const disabled = readOnly || pending || expired || held;
  const last = page === questions.length - 1;
  const completed = questions.filter((_, index) => answerReady(picked[index] ?? [], custom[index] ? other[index] : undefined)).length;
  const ready = completed === questions.length && (kind !== 'approval' || !custom[0] || (other[0]?.trim().length ?? 0) <= 2000);
  // The paging control sits outside the question, so after a page change focus goes to the new question
  // (its number keys keep working) instead of to a button that has just disappeared.
  useEffect(() => {
    if (paged.current) body.current?.querySelector('section')?.focus();
    paged.current = false;
  }, [page]);
  const goTo = (next: number) => {
    if (next < 0 || next >= questions.length) return;
    paged.current = true;
    setPage(next);
  };
  const send = async (answers: AskAnswer[]) => {
    if (disabled || sending.current) return;
    sending.current = true;
    setFailed(false);
    dismissKey(failureKey);
    setPending(true);
    try { await onSubmit(answers); }
    catch (error) {
      sending.current = false;
      setPending(false);
      // An answer for a question that is gone is a durable state of the question, not an operation failure.
      if (error instanceof Error && error.name === 'QuestionExpired') { setExpired(true); return; }
      setFailed(true);
      const holds = holdsAnswer(error);
      setHeld(holds);
      reportFailure(failureKey, error, { message: t.brainChat.askError, reload: releasing(onReload, () => setHeld(false)) });
    }
  };
  const submit = () => {
    if (ready) void send(questions.map((question, index) => ({
      header: question.header, selected: picked[index] ?? [],
      other: custom[index] && other[index]?.trim() ? other[index].trim() : undefined,
    })));
  };
  const state = readOnly ? <span className="flex items-start gap-2"><Lock size={14} className="mt-0.5 shrink-0" />{t.brainChat.askReadOnly}</span>
    : expired ? <span>{t.brainChat.askExpired}</span>
    : null;
  const approval = kind === 'approval';
  const original = questions[page];
  const question = approval ? { ...original, custom: true, options: [...original.options].sort((a, b) =>
    ['once', 'conversation', 'always', 'deny'].indexOf(a.id!) - ['once', 'conversation', 'always', 'deny'].indexOf(b.id!)) } : original;
  const multiple = questions.length > 1;
  const footerLeft = state ?? (multiple && last && !ready
    ? interpolate(t.brainChat.askProgress, { answered: completed, total: questions.length })
    : null);
  return <div data-testid="ask-card" aria-busy={pending} onKeyDown={event => {
    if (approval && event.key === 'Escape' && !disabled) {
      event.preventDefault(); event.stopPropagation();
      if (custom[page]) { setCustom(current => ({ ...current, [page]: false })); setOther(current => ({ ...current, [page]: '' })); }
      else { const deny = original.options.find(option => option.id === 'deny'); if (deny) void send([{ header: original.header, selected: [deny.label] }]); }
    } else if (event.key === 'Enter' && (event.ctrlKey || event.metaKey)) { event.preventDefault(); submit(); }
    else if (event.altKey && (event.key === 'ArrowLeft' || event.key === 'ArrowRight')) { event.preventDefault(); goTo(page + (event.key === 'ArrowLeft' ? -1 : 1)); }
  }} className="my-1.5 space-y-3 text-foreground">
    <div ref={body}>
      <QuestionSection key={page} question={question} index={page} total={questions.length}
        selected={picked[page] ?? []} otherOpen={custom[page] ?? false} other={other[page] ?? ''} disabled={disabled} approval={approval}
        onPick={label => {
          setPicked(current => ({ ...current, [page]: question.multiSelect
            ? (current[page] ?? []).includes(label) ? current[page].filter(pick => pick !== label) : [...(current[page] ?? []), label]
            : [label] }));
          if (!question.multiSelect) setCustom(current => ({ ...current, [page]: false }));
        }}
        onCustom={() => {
          setCustom(current => ({ ...current, [page]: question.multiSelect ? !current[page] : true }));
          if (!question.multiSelect) setPicked(current => ({ ...current, [page]: [] }));
        }}
        onOther={text => setOther(current => ({ ...current, [page]: text }))} />
    </div>
    <div className="flex flex-wrap items-center gap-x-3 gap-y-2">
      <div className="min-w-0 flex-1 text-meta text-muted-foreground" aria-live="polite">{footerLeft}</div>
      {approval && custom[page] ? <Button type="button" variant="ghost" disabled={disabled} onClick={() => { setCustom(current => ({ ...current, [page]: false })); setOther(current => ({ ...current, [page]: '' })); }}>{t.common.cancel}</Button> : null}
      {page > 0 ? <Button type="button" variant="ghost" disabled={pending} onClick={() => goTo(page - 1)}>{t.common.back}</Button> : null}
      {last
        ? <Button type="button" variant="accent" onClick={submit} disabled={disabled || !ready}>
            {pending ? t.brainChat.askSending : failed && !expired ? t.brainChat.askRetry : approval && custom[page] ? t.brainChat.ux.approvalSubmitInstead : t.brainChat.askSubmit}
          </Button>
        : <Button type="button" variant="accent" disabled={pending} onClick={() => goTo(page + 1)}>{t.pagination.next}</Button>}
    </div>
  </div>;
}
